<div class="create-store-modal">
  <!-- Close Button -->
  <button type="button" class="modal-close-btn" aria-label="Close" (click)="closeModal()" title="{{ 'Close' | translate }}">
    <i class="fi fi-rr-cross-small"></i>
  </button>

  <div class="create-store-layout">
    <!-- Left Sidebar Branding & Progress -->
    <aside class="create-store-sidebar">
      <div class="sidebar-top">
        <div class="brand-logo">
          <img src="https://cdn.estore5s.com/asset/estore-logo-transparent.png" alt="eStore" onError="this.src='assets/logo-white.png'" />
        </div>
        <div class="brand-badge">
          <i class="fi fi-rr-sparkles mr-1 text-amber-300"></i>
          <span>{{ "Store Builder" | translate }}</span>
        </div>
      </div>

      <!-- Step Stepper -->
      <div class="stepper-container" *ngIf="pageStep < 10">
        <div class="stepper-track">
          <div
            class="stepper-step"
            *ngFor="let step of createStorePages; let i = index"
            [class.completed]="pageStep > i + 1"
            [class.active]="pageStep === i + 1"
          >
            <div class="step-indicator">
              <div class="step-circle">
                <i class="fi fi-rr-check" *ngIf="pageStep > i + 1"></i>
                <span *ngIf="pageStep <= i + 1">{{ i + 1 }}</span>
              </div>
              <div class="step-line" *ngIf="i < createStorePages.length - 1"></div>
            </div>
            <div class="step-meta">
              <span class="step-label">{{ "STEP {0}" | translate : [i + 1] }}</span>
              <span class="step-name">{{ step.titleText | translate }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Trust Badges / Highlights -->
      <div class="sidebar-footer">
        <div class="feature-item">
          <i class="fi fi-rr-bolt text-amber-300"></i>
          <span>{{ "Instant Cloud Setup" | translate }}</span>
        </div>
        <div class="feature-item">
          <i class="fi fi-rr-shield-check text-emerald-300"></i>
          <span>{{ "Free SSL & CDN Included" | translate }}</span>
        </div>
        <div class="feature-item">
          <i class="fi fi-rr-magic-wand text-indigo-300"></i>
          <span>{{ "AI-Assisted Catalog" | translate }}</span>
        </div>
      </div>
    </aside>

    <!-- Right Content Panel -->
    <main class="create-store-main" [class.is-loading]="isInitialLoading || checkingSubdomain" [formGroup]="createStoreForm">
      
      <!-- Content Header (Steps 1 & 2) -->
      <header class="main-header" *ngIf="pageStep < 10">
        <div class="header-badge">
          <span>{{ "Step {0} of {1}" | translate : [pageStep, createStorePages.length] }}</span>
        </div>
        <h2 class="header-title">
          {{ createStorePages[pageStep - 1]?.titleText | translate }}
        </h2>
        <p class="header-subtext">
          {{ createStorePages[pageStep - 1]?.subText | translate }}
        </p>
      </header>

      <div class="main-body">
        <!-- ========================================== -->
        <!-- STEP 1: STORE DETAILS & SUBDOMAIN          -->
        <!-- ========================================== -->
        <section class="step-pane step-pane-1" *ngIf="pageStep === 1">
          <!-- Store Name Input -->
          <div class="form-field-card mb-4">
            <div class="d-flex justify-content-between align-items-center mb-1.5">
              <label class="field-label mb-0" for="store-name">
                <i class="fi fi-rr-shop text-primary mr-1.5"></i>
                {{ "Store Name" | translate }}
                <span class="text-danger">*</span>
              </label>
              <span class="field-hint text-muted text-xs">{{ "Min 5 characters" | translate }}</span>
            </div>
            
            <div class="modern-input-wrap">
              <input
                id="store-name"
                name="store-name"
                class="form-control modern-input"
                [class.is-invalid]="storeNameForm.touched && storeNameForm.invalid"
                type="text"
                placeholder="{{ 'e.g. My Wellness Store' | translate }}"
                formControlName="storeName"
                autocomplete="off"
              />
            </div>
            
            <div class="invalid-feedback d-block" *ngIf="storeNameForm.touched && storeNameForm.invalid">
              <span *ngIf="storeNameForm.errors?.required">{{ "Please enter a store name." | translate }}</span>
              <span *ngIf="storeNameForm.errors?.minlength">{{ "Store name must be at least 5 characters." | translate }}</span>
              <span *ngIf="storeNameForm.errors?.pattern">{{ "Store name contains invalid characters." | translate }}</span>
            </div>
          </div>

          <!-- Store Subdomain / Web Address -->
          <div class="form-field-card mb-4">
            <div class="d-flex justify-content-between align-items-center mb-1.5">
              <label class="field-label mb-0" for="store-subdomain">
                <i class="fi fi-rr-link-alt text-primary mr-1.5"></i>
                {{ "Store Web Address" | translate }}
                <span class="text-danger">*</span>
              </label>
              
              <button
                type="button"
                class="btn btn-link p-0 text-xs restricted-link"
                (click)="showRestrictedWordModal()"
              >
                <i class="fi fi-rr-info mr-1"></i>
                {{ "Restricted names" | translate }}
              </button>
            </div>

            <div class="subdomain-input-group">
              <span class="subdomain-prefix">https://</span>
              <input
                id="store-subdomain"
                type="text"
                class="form-control subdomain-input"
                placeholder="5s-wellness"
                maxlength="24"
                [class.is-invalid]="!subdomainForm.pristine && subdomainForm.invalid"
                [class.is-valid]="!subdomainForm.pristine && subdomainForm.valid"
                oninput="this.value = this.value.toLowerCase()"
                formControlName="subdomain"
                autocomplete="off"
              />
              <span class="subdomain-suffix">.{{ storeSubdomain }}</span>
            </div>

            <!-- Live URL Preview Pill -->
            <div class="live-url-pill mt-2" *ngIf="subdomainForm.value">
              <i class="fi fi-rr-globe text-primary mr-1.5"></i>
              <span class="live-url-text">
                https://<strong>{{ subdomainForm.value }}</strong>.{{ storeSubdomain }}
              </span>
            </div>

            <!-- Subdomain Validation Errors -->
            <div class="invalid-feedback d-block mt-1.5" *ngIf="subdomainForm.touched && subdomainForm.invalid">
              <span *ngIf="subdomainForm.errors?.required || subdomainForm.errors?.pattern">
                {{ "Please provide a valid subdomain (lowercase letters, numbers, and hyphens)." | translate }}
              </span>
              <span *ngIf="subdomainForm.errors?.taken">
                {{ "This subdomain is already taken. Please choose another." | translate }}
              </span>
              <span *ngIf="subdomainForm.errors?.minlength">
                {{ "Subdomain must be at least 5 characters." | translate }}
              </span>
              <span *ngIf="subdomainForm.errors?.serverSide">
                {{ subdomainForm.errors?.serverSide | translate }}
              </span>
            </div>
          </div>
        </section>

        <!-- ========================================== -->
        <!-- STEP 2: AI AUTOMATION & STORE SUMMARY      -->
        <!-- ========================================== -->
        <section class="step-pane step-pane-2" *ngIf="pageStep === 2">
          <!-- AI Content Generation Card -->
          <div
            class="ai-feature-card mb-4"
            [class.enabled]="aiGenerateContentEnabled"
            [class.disabled]="!aiGenerateStoreContentFeatureEnabled || !dealerCreditsLoaded || dealerCreditsLoading || dealerCredits < dealerMinimumCredit || !dealerCreditsValid"
          >
            <div class="ai-card-top d-flex justify-content-between align-items-start">
              <div class="d-flex align-items-start">
                <div class="ai-feature-icon mr-3">
                  <i class="fi fi-rr-magic-wand"></i>
                </div>
                <div>
                  <div class="d-flex align-items-center flex-wrap gap-2 mb-1">
                    <h5 class="ai-feature-title mb-0">{{ "Auto-generate Store Content with AI" | translate }}</h5>
                    <span class="ai-badge-recommend">{{ "Recommended" | translate }}</span>
                  </div>
                  <p class="ai-feature-desc mb-0">
                    {{ "Instantly create compelling product descriptions, directions for use, FAQs, and SEO metadata." | translate }}
                  </p>
                </div>
              </div>

              <!-- AI Switch Toggle -->
              <div class="ai-toggle-wrap ml-3">
                <div class="custom-control custom-switch custom-switch-lg">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    id="aiGenerateStoreContentSwitch"
                    [checked]="aiGenerateContentEnabled"
                    [disabled]="!aiGenerateStoreContentFeatureEnabled || !dealerCreditsLoaded || dealerCreditsLoading || dealerCredits < dealerMinimumCredit || !dealerCreditsValid"
                    (change)="onAiGenerateStoreContentChange($event.target.checked)"
                  />
                  <label class="custom-control-label" for="aiGenerateStoreContentSwitch"></label>
                </div>
              </div>
            </div>

            <!-- Credits Status Bar -->
            <div class="ai-credit-status mt-3 pt-2.5 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="d-flex align-items-center text-xs text-muted">
                <i class="fi fi-rr-coins text-amber-500 mr-1.5"></i>
                <span>{{ "Available Balance:" | translate }} <strong>{{ dealerCredits }} {{ "Credits" | translate }}</strong></span>
                <span class="mx-1.5 text-muted">•</span>
                <span>{{ "Required:" | translate }} <strong>{{ dealerMinimumCredit }} {{ "Credits" | translate }}</strong></span>
              </div>

              <div class="ai-time-estimate text-xs text-amber-700" *ngIf="aiGenerateContentEnabled">
                <i class="fi fi-rr-clock mr-1"></i>
                <span>{{ "Est. Time: ~{0} to {1} mins" | translate : [aiGenerationEstimatedTime.min, aiGenerationEstimatedTime.max] }}</span>
              </div>
            </div>

            <!-- Credit Warning -->
            <div class="ai-credit-warning mt-2.5 p-2 rounded text-xs" *ngIf="dealerCreditsLoaded && (dealerCredits < dealerMinimumCredit || !dealerCreditsValid)">
              <i class="fi fi-rr-exclamation text-amber-600 mr-1.5"></i>
              <span>{{ "You need at least {0} credits to enable automated AI generation." | translate : [dealerMinimumCredit] }}</span>
            </div>

            <!-- AI Multi-language checkboxes -->
            <div class="ai-languages-box mt-3 pt-3 border-top" *ngIf="aiGenerateContentEnabled && languageList?.length">
              <label class="field-sublabel mb-2">{{ "Generate content in languages:" | translate }}</label>
              <div class="d-flex flex-wrap gap-2">
                <label
                  class="lang-chip-checkbox"
                  *ngFor="let lang of languageList"
                  [class.selected]="isAiLanguageSelected(lang.language_code)"
                  [class.disabled]="isDefaultAiLanguage(lang.language_code)"
                >
                  <input
                    type="checkbox"
                    class="d-none"
                    [checked]="isAiLanguageSelected(lang.language_code)"
                    [disabled]="isDefaultAiLanguage(lang.language_code)"
                    (change)="toggleAiLanguage(lang.language_code)"
                  />
                  <i class="fi fi-rr-check mr-1 text-xs" *ngIf="isAiLanguageSelected(lang.language_code)"></i>
                  <span>{{ lang.language_name }}</span>
                  <small class="default-tag ml-1" *ngIf="isDefaultAiLanguage(lang.language_code)">({{ "Default" | translate }})</small>
                </label>
              </div>
            </div>
          </div>

          <!-- Store Confirmation Summary Card -->
          <div class="store-summary-card">
            <h6 class="summary-card-title mb-3">
              <i class="fi fi-rr-clipboard-list-check text-primary mr-1.5"></i>
              {{ "Store Setup Summary" | translate }}
            </h6>

            <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-label">{{ "Store Name" | translate }}</span>
                <span class="summary-value font-semibold">{{ storeNameForm.value }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">{{ "Web Address" | translate }}</span>
                <span class="summary-value text-primary font-semibold text-truncate">
                  https://{{ subdomainForm.value }}.{{ storeSubdomain }}
                </span>
              </div>
              <div class="summary-item">
                <span class="summary-label">{{ "Target Region" | translate }}</span>
                <span class="summary-value">
                  <img class="flag-img mr-1" *ngIf="countryForm.value?.country_flag" [src]="countryForm.value.country_flag" alt="" />
                  {{ countryForm.value?.country_name || "Malaysia" }} ({{ countryForm.value?.country_code || "MY" }})
                </span>
              </div>
              <div class="summary-item">
                <span class="summary-label">{{ "Store Language" | translate }}</span>
                <span class="summary-value">{{ languageForm.value?.language_name || "English" }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- ========================================== -->
        <!-- STEP 10: SUCCESS SCREEN                     -->
        <!-- ========================================== -->
        <section class="step-pane step-pane-success text-center py-4" *ngIf="pageStep === 10">
          <div class="success-sparkle-icon mb-3">
            <div class="sparkle-ring">
              <i class="fi fi-rr-check text-white"></i>
            </div>
          </div>

          <h3 class="success-title mb-2">{{ "Congratulations! 🎉" | translate }}</h3>
          <p class="success-subtitle mb-4 text-muted">
            {{ "Your store \"{0}\" has been successfully created and is ready for business!" | translate : [storeNameForm.value] }}
          </p>

          <div class="created-url-box p-3 rounded-lg mb-4 mx-auto" style="max-width: 440px;">
            <span class="text-xs text-muted d-block mb-1">{{ "Your Live Store URL" | translate }}</span>
            <code class="text-primary font-semibold text-sm">
              https://{{ createdStoreInfo?.store_name || subdomainForm.value }}.{{ storeSubdomain }}
            </code>
          </div>

          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-outline-secondary btn-lg px-4" (click)="closeModal()">
              {{ "Close" | translate }}
            </button>
            <button class="btn btn-primary btn-lg px-4 shadow-sm" (click)="gotoCustomizeStore()">
              <i class="fi fi-rr-magic-wand mr-1.5"></i>
              {{ "Customize Store Now" | translate }}
            </button>
          </div>
        </section>
      </div>

      <!-- Content Footer & Action Buttons -->
      <footer class="main-footer d-flex justify-content-between align-items-center" *ngIf="pageStep < 10">
        <div>
          <button
            type="button"
            class="btn btn-light btn-sm step-back-btn"
            *ngIf="pageStep > 1"
            (click)="stepBackButton()"
          >
            <i class="fi fi-rr-angle-left mr-1"></i>
            <span>{{ "Back" | translate }}</span>
          </button>
        </div>

        <div class="d-flex align-items-center gap-2">
          <button
            type="button"
            class="btn btn-light btn-sm"
            (click)="closeModal()"
          >
            <span>{{ "Cancel" | translate }}</span>
          </button>

          <!-- Step 1 Next Button -->
          <button
            type="button"
            class="btn btn-primary btn-sm px-4 d-inline-flex align-items-center"
            *ngIf="pageStep === 1"
            (click)="proceedButton()"
            [disabled]="checkingSubdomain || storeNameForm.invalid || subdomainForm.invalid"
          >
            <span class="spinner-border spinner-border-sm mr-1.5" *ngIf="checkingSubdomain"></span>
            <span>{{ (checkingSubdomain ? "Checking Address..." : "Next: AI Setup") | translate }}</span>
            <i class="fi fi-rr-angle-right ml-1.5" *ngIf="!checkingSubdomain"></i>
          </button>

          <!-- Step 2 Create Store Button -->
          <button
            type="button"
            class="btn btn-primary btn-sm px-4 d-inline-flex align-items-center btn-create-store"
            *ngIf="pageStep === 2"
            (click)="proceedButton()"
            [disabled]="isLoadingCreateStore || dealerCreditsLoading"
          >
            <span class="spinner-border spinner-border-sm mr-1.5" *ngIf="isLoadingCreateStore"></span>
            <i class="fi fi-rr-rocket mr-1.5" *ngIf="!isLoadingCreateStore"></i>
            <span>{{ (isLoadingCreateStore ? "Creating Your Store..." : "Create Store Now") | translate }}</span>
          </button>
        </div>
      </footer>
    </main>
  </div>

  <!-- Restricted Subdomains Modal -->
  <ng-template #restrictedWordsModalContent let-modal>
    <div class="modal-header border-bottom-0 pb-0">
      <h5 class="modal-title font-semibold">
        <i class="fi fi-rr-shield-exclamation text-amber-500 mr-1.5"></i>
        {{ "Restricted Domain Names" | translate }}
      </h5>
      <button type="button" class="close" aria-label="Close" (click)="modal.close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
    <div class="modal-body pt-2">
      <p class="text-muted text-sm mb-3">
        {{ "The following subdomains are reserved by the system and cannot be registered:" | translate }}
      </p>
      <div class="restricted-names-grid">
        <span class="restricted-badge" *ngFor="let item of restrictedWords">{{ item }}</span>
      </div>
    </div>
    <div class="modal-footer border-top-0 pt-0">
      <button type="button" class="btn btn-primary btn-sm" (click)="modal.close()">
        {{ "Got it" | translate }}
      </button>
    </div>
  </ng-template>
</div>